import React, { useState } from 'react';
import { skillsService, ImportResult } from '../../../services/skillsService';

interface SkillsImporterProps {
  onError: (error: string | null) => void;
}

const SkillsImporter: React.FC<SkillsImporterProps> = ({ onError }) => {
  const [importData, setImportData] = useState<string>('');
  const [importing, setImporting] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [importResults, setImportResults] = useState<ImportResult | null>(null);
  const [exampleOpen, setExampleOpen] = useState<boolean>(true);

  const handleImportChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setImportData(e.target.value);
  };

  const handleExport = async () => {
    try {
      console.log('Exporting skills hierarchy...');
      setExportLoading(true);
      onError(null);
      
      const data = await skillsService.getSkillsHierarchy();
      console.log('Hierarchy data received for export:', data);
      
      // Format data for display
      const formattedData = JSON.stringify(data, null, 2);
      setImportData(formattedData);
      
      // Create download file
      const blob = new Blob([formattedData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `skills-hierarchy-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (err) {
      console.error('Error exporting skills hierarchy:', err);
      onError(err instanceof Error ? err.message : 'Failed to export skills hierarchy');
    } finally {
      setExportLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      onError('Please paste valid JSON data for import');
      return;
    }

    try {
      console.log('Importing skills hierarchy...');
      setImporting(true);
      setImportResults(null);
      onError(null);
      
      let data: any;
      try {
        data = JSON.parse(importData) as any;
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        onError('Invalid JSON format. Please check your data.');
        setImporting(false);
        return;
      }
      
      console.log('Starting import with data:', data);
      const results = await skillsService.importSkillsHierarchy(data);
      console.log('Import results:', results);
      setImportResults(results);
      
    } catch (err) {
      console.error('Error importing skills hierarchy:', err);
      onError(err instanceof Error ? err.message : 'Failed to import skills hierarchy');
    } finally {
      setImporting(false);
    }
  };

  const renderImportResults = () => {
    if (!importResults) return null;
    
    const { stats, errors } = importResults;
    const total = stats.categories.created + stats.skills.created + stats.subskills.created;
    const hasErrors = errors && errors.length > 0;
    
    return (
      <div className="border rounded p-4 mt-4 bg-gray-50">
        <h3 className="font-semibold text-lg mb-2">Import Results</h3>
        <div className="mb-2">
          <p className="text-green-700">Successfully imported {total} items:</p>
          <ul className="ml-6 list-disc">
            <li>Categories: {stats.categories.created}</li>
            <li>Skills: {stats.skills.created}</li>
            <li>Subskills: {stats.subskills.created}</li>
          </ul>
        </div>
        
        {hasErrors && (
          <div className="mt-4">
            <p className="text-red-700">Errors encountered ({errors.length}):</p>
            <div className="max-h-40 overflow-y-auto mt-2 border border-red-200 p-2 rounded bg-red-50">
              <ul className="ml-6 list-disc">
                {errors.map((error: string, index: number) => (
                  <li key={index} className="text-sm text-red-600">{error}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-6">Import & Export Skills</h2>
      
      <div className="grid grid-cols-1 gap-6">
        <div className="p-4 border rounded">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-lg">Import/Export Data</h3>
            <div className="space-x-2">
              <button 
                className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
                onClick={handleExport}
                disabled={exportLoading}
              >
                {exportLoading ? 'Exporting...' : 'Export Current Data'}
              </button>
              <button 
                className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-300"
                onClick={handleImport}
                disabled={importing || !importData.trim()}
              >
                {importing ? 'Importing...' : 'Import Data'}
              </button>
            </div>
          </div>
          
          <div className="mb-3">
            <p className="text-sm text-gray-600 mb-2">
              Paste JSON data below to import, or click Export to download the current skills hierarchy.
            </p>
            <div className="mb-3">
              <button className="text-blue-700 text-sm underline" onClick={() => setExampleOpen(!exampleOpen)}>
                {exampleOpen ? 'Hide examples' : 'Show examples'}
              </button>
              {exampleOpen && (
                <div className="mt-2 p-3 border rounded bg-white text-sm">
                  <p className="font-semibold mb-2">Examples (inspired by Grammar DB):</p>
                  <p className="text-gray-700 mb-1">1) Import a Skill by parent ID</p>
                  <pre className="whitespace-pre-wrap bg-gray-50 p-2 rounded border overflow-x-auto">{`{
  "data": {
    "skills": [{
      "name": "Presentations",
      "parent_id": "<CATEGORY_ID>"
    }]
  }
}`}</pre>
                  <p className="text-gray-700 mt-3 mb-1">2) Import a Skill by parent name</p>
                  <pre className="whitespace-pre-wrap bg-gray-50 p-2 rounded border overflow-x-auto">{`{
  "data": {
    "skills": [{
      "name": "Presentations",
      "parent_name": "Express Yourself"
    }]
  }
}`}</pre>
                  <p className="text-gray-700 mt-3 mb-1">3) Import a Subskill by parent SKILL ID</p>
                  <pre className="whitespace-pre-wrap bg-gray-50 p-2 rounded border overflow-x-auto">{`{
  "data": {
    "sub_skills": [{
      "parent_id": "<SKILL_ID>",
      "sub_skill_name": "Signposting",
      "examples": [{"text":"example","level":"beginner","context":"meeting"}]
    }]
  }
}`}</pre>
                  <p className="text-gray-700 mt-3 mb-1">4) Import a Subskill by parent SKILL name</p>
                  <pre className="whitespace-pre-wrap bg-gray-50 p-2 rounded border overflow-x-auto">{`{
  "data": {
    "sub_skills": [{
      "parent_name": "Presentations",
      "sub_skill_name": "Signposting",
      "examples": [{"text":"example","level":"beginner","context":"meeting"}]
    }]
  }
}`}</pre>
                  <p className="text-gray-700 mt-3">Rules:</p>
                  <ul className="list-disc ml-6 text-gray-700">
                    <li>Parent must exist (CATEGORY for skills, SKILL for subskills).</li>
                    <li>Names must be globally unique (case-insensitive) across categories, skills, subskills.</li>
                    <li>On validation errors, you will see errors listed in the results.</li>
                  </ul>
                </div>
              )}
            </div>
            <textarea 
              className="w-full h-80 p-3 border rounded font-mono text-sm"
              placeholder="Paste JSON data here..."
              value={importData}
              onChange={handleImportChange}
              disabled={importing}
            />
          </div>
          
          {importing && (
            <div className="flex justify-center items-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-talaera-blue mr-2"></div>
              <span>Importing data...</span>
            </div>
          )}
          
          {renderImportResults()}
        </div>
      </div>
    </div>
  );
};

export default SkillsImporter; 